/**
 * The Basic vs Pro comparison behind the "Why go Pro" sheet.
 *
 * Derived from lib/proFeatureRows (the owner's rows, 2026-09-11) so the sheet,
 * the paywall, Settings and the website all say the same thing. Every row
 * mirrors a real gate in the current build; when a gate moves or a limit
 * changes, the row moves in proFeatureRows and everything follows.
 *
 * Cells are pictures first (a tick, a dash, a figure), words as the fallback:
 * a "1" against "Unlimited" tells the story faster than a sentence would.
 */
import { PRO_FEATURE_GROUPS, type ProCell } from "@/lib/proFeatureRows";

export type WhyProCell =
  | { kind: "yes" }
  | { kind: "no" }
  /** A short figure or word, at most a couple of tokens. */
  | { kind: "text"; label: string };

export interface WhyProRow {
  /** Stable id, doubles as the test id suffix. */
  id: string;
  /** Feature name as the trainer knows it. */
  label: string;
  /** One line under the label, when the name alone does not say enough. */
  detail?: string;
  /** The group heading this row sits under (Pokédex, Friends and trades...). */
  group: string;
  basic: WhyProCell;
  pro: WhyProCell;
}

function toCell(c: ProCell): WhyProCell {
  if (c === true) return { kind: "yes" };
  if (c === false) return { kind: "no" };
  return { kind: "text", label: c };
}

export const WHY_PRO_ROWS: WhyProRow[] = PRO_FEATURE_GROUPS.flatMap((g) =>
  g.rows.map((r) => ({
    id: r.key,
    label: r.title,
    detail: r.detail,
    group: g.heading,
    basic: toCell(r.basic),
    pro: toCell(r.pro),
  })),
);

/** Rows in display order, grouped under their headings. */
export const WHY_PRO_GROUPS: Array<{ heading: string; rows: WhyProRow[] }> = PRO_FEATURE_GROUPS.map((g) => ({
  heading: g.heading,
  rows: WHY_PRO_ROWS.filter((r) => r.group === g.heading),
}));
